import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BadgeCheck,
  FileCode2,
  MapPin,
  PackageSearch,
  Printer,
  RefreshCw,
  Tag,
} from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useContexto } from '@/hooks/useContexto';
import { useParamUrl } from '@/hooks/useUrlEstado';
import { useAbrirNoEdge } from '@/hooks/useAbrirNoEdge';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { CabecalhoPagina, Cartao, CorpoCartao, Esqueleto } from '@/componentes/ui/primitivos';
import { Segmentado } from '@/componentes/ui/segmentado';
import {
  CSS_CONFERENCIA_CEGA,
  ConferenciaCegaPapel,
} from '@/componentes/recebimento/ConferenciaCegaPapel';
import { PainelAprovacao } from '@/componentes/recebimento/PainelAprovacao';
import { PainelEtiquetasAlocacao } from '@/componentes/recebimento/PainelEtiquetasAlocacao';
import { PainelPendencias } from '@/componentes/recebimento/PainelPendencias';
import { PainelAlocacao } from '@/componentes/recebimento/PainelAlocacao';
import { PainelImportarXml } from '@/componentes/recebimento/PainelImportarXml';

/**
 * Recebimento — imprimir a conferência cega e aprovar o recebimento.
 *
 * Duas abas, uma entrada de menu. A primeira espelha o formulário
 * `RECEBIMENTOS_VOLUMES` do Procfit: informa-se o número e sai o papel A4 que o
 * conferente leva para o box. A segunda espelha `APROVACOES_RECEB_VOLUMES`, e é
 * o que fecha o ciclo depois que o coletor terminou a contagem.
 *
 * São duas telas no Procfit e uma só aqui porque são as duas metades do MESMO
 * trabalho — quem imprime é quem depois vai aprovar. Separá-las em dois itens
 * de menu não daria mais clareza, daria mais lugar para procurar.
 *
 * CEGA NA TELA TAMBÉM
 * -------------------
 * A primeira versão mostrava as quantidades de pedido e de nota na tela, com o
 * argumento de que quem imprime é o supervisor. Estava errado: o número vaza
 * por cima do ombro, por um print, por um F12 — e a conferência deixa de ser
 * cega justamente no momento em que isso importa.
 *
 * Agora a quantidade não sai nem do servidor. O que identifica o item é o
 * código, o nome e o final do EAN; o que situa o recebimento é a nota fiscal e
 * o fornecedor. Nada disso conta nada.
 */

type Aba = 'cega' | 'aprovar' | 'etiquetas' | 'alocar' | 'xml';

interface ItemRecebimento {
  produto: number;
  descricao: string | null;
  /** Os 8 primeiros dígitos do EAN, como o relatório do Procfit imprime. */
  ean8: string | null;
  tipo: string | null;
  qtdeEmbalagem: number | null;
  pedidoCompra: number | null;
}

interface RecebimentoDetalhe {
  cabecalho: {
    recebimento: number;
    data: string | null;
    empresa: number | null;
    centroEstoque: number | null;
    fornecedor: string | null;
    fornecedorCodigo: number | null;
    notaFiscal: number | null;
    serie: string | null;
    emissao: string | null;
    volumes: number;
    notas: number;
  };
  itens: ItemRecebimento[];
  totalItens: number;
}

interface RecebimentoDaLista {
  recebimento: number;
  data: string | null;
  fornecedor: string | null;
  notaFiscal: number | null;
  itens: number;
}

function dataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}

export function Recebimento(): JSX.Element {
  const { centro, empresa } = useContexto();
  const [selecionado, definirSelecionado] = useParamUrl('recebimento');
  /* A aba escolhida vive na URL: o link que alguém manda abre onde mandou. */
  const [abaUrl, definirAbaUrl] = useParamUrl('aba');
  const aba: Aba =
    abaUrl === 'aprovar'
      ? 'aprovar'
      : abaUrl === 'etiquetas'
        ? 'etiquetas'
        : abaUrl === 'alocar'
          ? 'alocar'
          : abaUrl === 'xml'
            ? 'xml'
            : 'cega';
  const definirAba = (nova: Aba): void => definirAbaUrl(nova);
  const numeroSelecionado = Number(selecionado) || null;

  const lista = useQuery<{ centro: number; recebimentos: RecebimentoDaLista[] }>({
    queryKey: ['recebimentos', centro],
    queryFn: () =>
      api.get<{ centro: number; recebimentos: RecebimentoDaLista[] }>('/api/recebimentos', {
        centro,
        limite: 40,
      }),
    enabled: centro !== null,
    staleTime: 60_000,
  });

  const detalhe = useQuery<RecebimentoDetalhe>({
    queryKey: ['recebimento', numeroSelecionado],
    queryFn: () => api.get<RecebimentoDetalhe>(`/api/recebimentos/${numeroSelecionado}`),
    enabled: numeroSelecionado !== null,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      <CabecalhoPagina
        sobrelinha="Operação"
        titulo="Recebimento"
        descricao={
          aba === 'cega'
            ? 'Informe o recebimento e imprima a conferência cega — o papel sai sem as quantidades, para o conferente contar.'
            : aba === 'aprovar'
              ? 'Depois que o coletor terminou a contagem, aprove o recebimento — a aprovação é gravada no ERP com o seu usuário.'
              : aba === 'etiquetas'
                ? 'As etiquetas de 100 × 50 mm que o coletor bipa ao guardar. Pendentes são as que ainda não foram bipadas.'
                : aba === 'xml'
                  ? 'Envie o XML da NF-e do fornecedor, ou só a chave, e o ERP gera a NF de compra.'
                  : 'Bipe a etiqueta e informe a posição. A alocação dá saída no recebimento e entrada no endereço.'
        }
        icone={<PackageSearch className="h-5 w-5" />}
      />

      {/*
        AS ABAS FICAM NUMA LINHA SÓ DELAS, E NÃO NAS AÇÕES DO CABEÇALHO.

        Estavam no `acoes`, ao lado do campo "Recebimento nº" — que não existe
        na aba Aprovar. Ao trocar de aba o campo sumia e o controle inteiro
        escorregava para a direita: a pessoa clicava em "Aprovar" e as abas
        saíam de debaixo do cursor. Controle de navegação não pode se mexer
        quando é usado.

        Por isso a linha existe sempre, com as abas ancoradas à esquerda; só o
        que está à DIREITA muda conforme a aba.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <Segmentado
          tamanho="pequeno"
          valor={aba}
          aoMudar={definirAba}
          rotuloAcessivel="O que fazer no recebimento"
          opcoes={[
            {
              valor: 'cega',
              rotulo: 'Conferência cega',
              icone: <Printer className="h-3.5 w-3.5" />,
            },
            {
              valor: 'aprovar',
              rotulo: 'Aprovar',
              icone: <BadgeCheck className="h-3.5 w-3.5" />,
            },
            {
              valor: 'etiquetas',
              rotulo: 'Etiquetas alocação',
              icone: <Tag className="h-3.5 w-3.5" />,
            },
            {
              valor: 'alocar',
              rotulo: 'Alocação',
              icone: <MapPin className="h-3.5 w-3.5" />,
            },
            {
              valor: 'xml',
              rotulo: 'Importar XML',
              icone: <FileCode2 className="h-3.5 w-3.5" />,
            },
          ]}
        />

        {aba === 'cega' || aba === 'etiquetas' ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {aba === 'etiquetas' && numeroSelecionado !== null ? (
              <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirSelecionado(null)}>
                <Tag className="h-4 w-4" />
                Aguardando etiqueta
              </Botao>
            ) : null}
            <AbrirPorNumero aoAbrir={(n) => definirSelecionado(String(n))} />
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => void lista.refetch()}
              carregando={lista.isFetching}
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </Botao>
          </div>
        ) : null}
      </div>

      {aba === 'aprovar' ? <PainelAprovacao centro={centro} empresa={empresa} /> : null}
      {aba === 'alocar' ? <PainelAlocacao empresa={empresa} centro={centro} /> : null}
      {aba === 'xml' ? <PainelImportarXml empresa={empresa} /> : null}
      {aba === 'etiquetas' ? (
        <PainelEtiquetasAlocacao
          recebimento={numeroSelecionado}
          cabecalho={detalhe.data?.cabecalho ?? null}
          empresa={empresa}
          centro={centro}
          aoAbrir={(n) => definirSelecionado(String(n))}
        />
      ) : null}

      {/* Lista e detalhe rolam cada um por si — ver o comentário em Etiquetas.tsx. */}
      <div
        className={cn(
          'grid items-start gap-3 lg:h-[calc(100vh-190px)] lg:min-h-[420px] lg:grid-cols-[300px_minmax(0,1fr)] lg:items-stretch',
          /*
            A aba inativa fica MONTADA e escondida, não desmontada: o papel da
            impressão vive aqui dentro, e desmontá-lo faria perder a seleção e
            o conteúdo já carregado a cada troca de aba.
          */
          aba !== 'cega' && 'hidden',
        )}
      >
        <Cartao className="nao-imprimir flex flex-col lg:min-h-0 lg:overflow-hidden">
          <div className="flex items-center gap-2 border-b border-borda px-3 py-2.5">
            <span className="text-[13px] font-bold">Últimos recebimentos</span>
          </div>
          <CorpoCartao className="min-h-0 flex-1 space-y-2 overflow-y-auto">
            {lista.isLoading ? (
              <Esqueleto className="h-64" />
            ) : lista.isError ? (
              <EstadoErro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
            ) : (lista.data?.recebimentos.length ?? 0) === 0 ? (
              <EstadoVazio
                titulo="Nenhum recebimento com itens"
                detalhe="Nos últimos 60 dias, nenhum recebimento deste centro tem itens esperados para conferir."
              />
            ) : (
              <ul className="divide-y divide-borda">
                {lista.data?.recebimentos.map((r) => (
                  <li key={r.recebimento}>
                    <button
                      type="button"
                      onClick={() => definirSelecionado(String(r.recebimento))}
                      title={r.fornecedor ?? ''}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors',
                        numeroSelecionado === r.recebimento
                          ? 'bg-primaria-suave'
                          : 'hover:bg-superficie2',
                      )}
                    >
                      <span
                        className={cn(
                          'tabular shrink-0 font-mono text-[13px] font-bold',
                          numeroSelecionado === r.recebimento && 'text-primaria',
                        )}
                      >
                        {r.recebimento}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[11.5px] font-semibold">
                          {r.fornecedor ?? 'sem fornecedor'}
                        </span>
                        <span className="tabular block text-[10.5px] text-textoFraco">
                          {dataHora(r.data)}
                          {r.notaFiscal ? ` · NF ${r.notaFiscal}` : ''}
                        </span>
                      </span>
                      <span className="tabular shrink-0 text-[11px] font-semibold text-textoFraco">
                        {numero(r.itens)} it
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CorpoCartao>
        </Cartao>

        <div className="lg:min-h-0 lg:overflow-y-auto lg:pr-1">
          {/*
            Sem recebimento escolhido, o espaço vira a FILA DA EMPRESA em vez de
            um cartaz pedindo um número. Quem abre esta tela raramente sabe o
            número: sabe que tem coisa parada, e é isso que o painel responde.
          */}
          {numeroSelecionado === null ? (
            <PainelPendencias
              empresa={empresa}
              centro={centro}
              aoAbrirRecebimento={(n) => definirSelecionado(String(n))}
            />
          ) : detalhe.isLoading ? (
            <Esqueleto className="h-[420px]" />
          ) : detalhe.isError ? (
            <Cartao>
              <CorpoCartao>
                <EstadoErro erro={detalhe.error} aoTentarDeNovo={() => void detalhe.refetch()} />
                {detalhe.error instanceof ErroHttp && detalhe.error.status === 404 ? (
                  <p className="mt-2 text-[12.5px] text-textoFraco">
                    Se o número está certo, o recebimento pode ser de outra empresa — troque a
                    empresa no seletor do topo.
                  </p>
                ) : null}
              </CorpoCartao>
            </Cartao>
          ) : detalhe.data ? (
            <Detalhe dados={detalhe.data} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** O campo de número — o caminho mais curto até o papel. */
function AbrirPorNumero({ aoAbrir }: { aoAbrir: (n: number) => void }): JSX.Element {
  const [valor, definirValor] = React.useState('');
  return (
    <form
      className="flex items-center gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        const n = Number(valor.replace(/\D/g, ''));
        if (n > 0) {
          aoAbrir(n);
          definirValor('');
        }
      }}
    >
      {/*
        O campo é a porta de entrada da tela — quem chega aqui já tem o número
        do recebimento na mão. Antes ele se confundia com o resto da barra;
        agora tem moldura própria, rótulo alinhado e foco visível, sem virar
        um bloco chamativo no topo.
      */}
      <label
        htmlFor="num-recebimento"
        className="flex items-center gap-2 rounded-xl border border-bordaForte bg-superficie2 py-1 pl-3 pr-1 shadow-xs focus-within:border-primaria focus-within:ring-2 focus-within:ring-primaria/25"
      >
        <span className="whitespace-nowrap text-[11.5px] font-bold uppercase tracking-wide text-textoFraco">
          Recebimento nº
        </span>
        <input
          id="num-recebimento"
          value={valor}
          onChange={(e) => definirValor(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          placeholder="00000"
          autoComplete="off"
          className="tabular min-h-[34px] w-[92px] rounded-lg border border-borda bg-superficie px-2 text-center font-mono text-[15px] font-bold outline-none placeholder:font-normal placeholder:text-textoFraco/50"
        />
        <Botao variante="secundaria" tamanho="pequeno" type="submit" disabled={valor === ''}>
          Abrir
        </Botao>
      </label>
    </form>
  );
}

function Detalhe({ dados }: { dados: RecebimentoDetalhe }): JSX.Element {
  const { cabecalho, itens } = dados;
  const papel = React.useRef<HTMLDivElement>(null);
  // O A4 sai pelo Edge, com prévia e escolha de impressora (ver abrirPapelNoEdge).
  const edge = useAbrirNoEdge();

  return (
    <div className="space-y-3">
      <Cartao>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-borda px-4 py-3">
          <div className="min-w-0">
            <p className="rotulo">Recebimento</p>
            <h2 className="tabular font-mono text-[24px] font-bold leading-tight">
              {cabecalho.recebimento}
            </h2>
            <p className="mt-0.5 truncate text-[12.5px] text-textoFraco">
              {cabecalho.fornecedor ?? 'sem fornecedor'} · {dataHora(cabecalho.data)}
            </p>
          </div>
          <Botao
            variante="primaria"
            onClick={() =>
              papel.current &&
              void edge.abrir({
                modo: 'papel',
                conteudo: papel.current,
                titulo: `Conferencia cega ${cabecalho.recebimento}`,
                css: CSS_CONFERENCIA_CEGA,
              })
            }
            carregando={edge.abrindo}
            disabled={itens.length === 0}
            title="Abre a conferência cega no Edge, com a prévia e a escolha da impressora A4."
          >
            <Printer className="h-4 w-4" />
            Imprimir conferência cega
          </Botao>
        </div>

        <CorpoCartao className="space-y-3">
          {edge.erro ? (
            <p className="flex items-start gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[12.5px] text-status-divergencia">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {edge.erro}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
            <Dado
              rotulo="Fornecedor"
              valor={`${cabecalho.fornecedorCodigo ?? ''} ${cabecalho.fornecedor ?? '—'}`.trim()}
            />
            <Dado
              rotulo="Nota fiscal"
              valor={`${cabecalho.notaFiscal ?? '—'}${cabecalho.serie ? ` / ${cabecalho.serie}` : ''}`}
            />
            <Dado rotulo="Emissão" valor={dataHora(cabecalho.emissao)} />
            <Dado rotulo="Volumes" valor={cabecalho.volumes ? numero(cabecalho.volumes) : '—'} />
            <Dado rotulo="Itens a conferir" valor={numero(dados.totalItens)} />
          </div>

          {cabecalho.notas > 1 ? (
            <p className="rounded-lg bg-superficie2 px-3 py-2 text-[12.5px]">
              Este recebimento tem <strong>{cabecalho.notas} notas fiscais</strong>. O papel mostra
              a primeira no cabeçalho e lista os itens de todas.
            </p>
          ) : null}

          {/*
            A tela mostra o MESMO que o papel: identificação, nunca contagem.
            Se a quantidade aparecesse aqui, bastaria alguém abrir a tela ao
            lado do conferente para a conferência deixar de ser cega.
          */}
          <p className="flex items-start gap-2 rounded-lg bg-superficie2 px-3 py-2 text-[12px] text-textoFraco">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Conferência <strong>cega</strong>: nem esta tela nem o papel mostram as quantidades
              esperadas. O conferente conta e anota; a comparação é feita depois, no lançamento.
            </span>
          </p>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-[12.5px]">
              <thead>
                <tr className="border-b border-borda text-left">
                  <th className="rotulo py-1.5 pr-2">Código</th>
                  <th className="rotulo py-1.5 pr-2">Produto</th>
                  <th className="rotulo py-1.5 pr-2">EAN</th>
                  <th className="rotulo py-1.5 pr-2">Tipo</th>
                  <th className="rotulo py-1.5 pr-2 text-right">Qtd emb.</th>
                  <th className="rotulo py-1.5">Pedido</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((i) => (
                  <tr key={i.produto} className="border-b border-borda/60">
                    <td className="tabular py-1.5 pr-2 font-mono font-semibold">{i.produto}</td>
                    <td className="max-w-[320px] truncate py-1.5 pr-2" title={i.descricao ?? ''}>
                      {i.descricao ?? '—'}
                    </td>
                    <td className="tabular py-1.5 pr-2 font-mono text-[11.5px] text-textoFraco">
                      {i.ean8 ?? '—'}
                    </td>
                    <td className="py-1.5 pr-2 text-[11.5px]">{i.tipo ?? '—'}</td>
                    <td className="tabular py-1.5 pr-2 text-right text-[11.5px]">
                      {i.qtdeEmbalagem ?? '—'}
                    </td>
                    <td className="tabular py-1.5 text-[11.5px] text-textoFraco">
                      {i.pedidoCompra ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CorpoCartao>
      </Cartao>

      {/*
        O papel fica montado fora da tela — é ele que a impressão copia.
        Escondido com `display:none` não teria altura para medir e sairia em
        branco; por isso vai para fora da viewport.
      */}
      <style>{CSS_CONFERENCIA_CEGA}</style>
      <div className="pointer-events-none h-0 overflow-hidden" aria-hidden>
        <div ref={papel} className="absolute left-[-10000px] top-0 w-[210mm]">
          <ConferenciaCegaPapel
            cabecalho={{
              ...cabecalho,
              empresa: cabecalho.empresa,
              centroEstoque: cabecalho.centroEstoque,
            }}
            /* Sem quantidades: o papel é cego por construção, não por CSS. */
            itens={itens}
          />
        </div>
      </div>
    </div>
  );
}

function Dado({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <span className="min-w-0">
      <span className="rotulo block">{rotulo}</span>
      <span className="tabular block truncate text-[13.5px] font-semibold">{valor}</span>
    </span>
  );
}
